import React, { useEffect, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useNavigate } from 'react-router';
import { getProjectById, restoreProject, getProjectUsers } from '../../api/projects';
import { getProjectActionItems } from '../../api/actionItems';
import { getProjectDailyUpdates } from '../../api/dailyUpdates';
import { getProjectMeetingNotes } from '../../api/meetingNotes';
import { getUserProfile } from '../../api/users';
import { getProjectDocuments } from '../../api/documents';
import { ApiError } from '../../config/api';
import { ConfirmationDialog } from '../ConfirmationDialog';
import { CloneProjectModal } from '../CloneProjectModal';
import { DownloadLogPagination } from '../DownloadLogs/DownloadLogPagination';
import { TabActionItems } from './TabActionItems';
import { TabDailyUpdates } from './TabDailyUpdates';
import { TabMeetingNotes } from './TabMeetingNotes';
import { TabProjectPlan } from './TabProjectPlan';
import { ProjectLocations } from './ProjectLocations';
import { ProjectTeam } from './ProjectTeam';
import { ArchivedProjectProvider } from '../../context/ArchivedProjectContext';
import { toast } from 'react-toastify';
import { FaArchive, FaClipboardList, FaLock } from 'react-icons/fa';
import { LoadingSpinner } from '../LoadingSpinner';
import { useProjectsPage } from '../../state/projects';

interface ArchivedProjectDetailProps {
  projectId: string;
}

type TabType = 'overview' | 'action-items' | 'project-plan-2' | 'daily-updates' | 'meeting-notes' | 'team' | 'documents' | 'locations';

const getReadableStatus = (status?: string): string => {
  if (!status) return 'Not Started';
  const statusMap: { [key: string]: string } = {
    'NOT_STARTED': 'Not Started',
    'IN_PROGRESS': 'In Progress',
    'ON_HOLD': 'On Hold',
    'COMPLETED': 'Completed',
    'CANCELLED': 'Cancelled',
    'ARCHIVED': 'Archived',
  };
  return statusMap[status] || status;
};

export const ArchivedProjectDetail: React.FC<ArchivedProjectDetailProps> = ({ projectId }) => {
  const { keycloak: { token, tokenParsed } } = useKeycloak();
  const navigate = useNavigate();
  const { refreshProjects } = useProjectsPage();
  const [project, setProject] = useState<ProjectInterface | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [actionItems, setActionItems] = useState<ProjectActionItemsInterface | null>(null);
  const [dailyUpdates, setDailyUpdates] = useState<ProjectDailyUpdatesInterface | null>(null);
  const [meetingNotes, setMeetingNotesData] = useState<ProjectMeetingNotesInterface | null>(null);
  const [projectUsers, setProjectUsers] = useState<ProjectUsersResponse | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [projectDocuments, setProjectDocuments] = useState<any>(null);
  const [tabsLoading, setTabsLoading] = useState(false);
  const [ownerName, setOwnerName] = useState<string | null>(null);
  const [pagination, setPagination] = useState<{ [key in TabType]: { page: number; size: number; total: number } }>({
    'overview': { page: 1, size: 25, total: 1 },
    'action-items': { page: 1, size: 25, total: 0 },
    'project-plan-2': { page: 1, size: 25, total: 0 },
    'daily-updates': { page: 1, size: 25, total: 0 },
    'meeting-notes': { page: 1, size: 25, total: 0 },
    'team': { page: 1, size: 25, total: 0 },
    'documents': { page: 1, size: 25, total: 0 },
    'locations': { page: 1, size: 25, total: 0 },
  });

  useEffect(() => {
    const loadProject = async () => {
      if (!token) return;
      setIsLoading(true);
      setError(null);
      try {
        const data = await getProjectById(token, projectId, undefined, { archived: true });
        setProject(data);
      } catch (e) {
        const status = e instanceof Error && 'status' in e ? (e as ApiError).status : undefined;
        if (status === 403) {
          setError('You do not have permission to view this archived project');
        } else if (status === 404) {
          setError('This archived project was not found');
        } else {
          setError('Failed to load archived project');
        }
        console.error('Error loading archived project:', e);
      } finally {
        setIsLoading(false);
      }
    };

    loadProject();
  }, [token, projectId]);

  useEffect(() => {
    const loadOwnerProfile = async () => {
      if (!token || !project?.owner_id) return;
      try {
        const profile = await getUserProfile(token, project.owner_id);
        setOwnerName(`${profile.first_name} ${profile.last_name}`);
      } catch (e) {
        console.error('Failed to load owner profile:', e);
      }
    };

    loadOwnerProfile();
  }, [token, project?.owner_id]);

  const loadTabData = async (tab: TabType) => {
    if (!token) return;
    setTabsLoading(true);
    try {
      const paginationState = pagination[tab];
      if (tab === 'action-items') {
        const data = await getProjectActionItems(token, projectId, { archived: true, page: paginationState.page, size: paginationState.size });
        setActionItems(data);
        setPagination(prev => ({ ...prev, [tab]: { ...prev[tab], total: data.total || 0 } }));
      } else if (tab === 'daily-updates') {
        const data = await getProjectDailyUpdates(token, projectId, { archived: true, page: paginationState.page, size: paginationState.size });
        setDailyUpdates(data);
        setPagination(prev => ({ ...prev, [tab]: { ...prev[tab], total: data.total || 0 } }));
      } else if (tab === 'meeting-notes') {
        const data = await getProjectMeetingNotes(token, projectId, { archived: true, page: paginationState.page, size: paginationState.size });
        setMeetingNotesData(data);
        setPagination(prev => ({ ...prev, [tab]: { ...prev[tab], total: data.total || 0 } }));
      } else if (tab === 'team') {
        const data = await getProjectUsers(token, projectId, { archived: true, page: paginationState.page, size: paginationState.size });
        setProjectUsers(data);
        setPagination(prev => ({ ...prev, [tab]: { ...prev[tab], total: data.total || 0 } }));
      } else if (tab === 'documents') {
        const data = await getProjectDocuments(token, projectId, { archived: true, page: paginationState.page, size: paginationState.size });
        setProjectDocuments(data);
        setPagination(prev => ({ ...prev, [tab]: { ...prev[tab], total: data.total || 0 } }));
      }
    } catch (e) {
      console.error(`Failed to load ${tab} data:`, e);
      toast.error(`Failed to load ${tab} data`);
    } finally {
      setTabsLoading(false);
    }
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setPagination(prev => ({ ...prev, [tab]: { ...prev[tab], page: 1 } }));
    if (tab !== 'overview') {
      loadTabData(tab);
    }
  };

  const handlePageChange = (tab: TabType, newPage: number) => {
    setPagination(prev => ({ ...prev, [tab]: { ...prev[tab], page: newPage } }));
    loadTabData(tab);
  };

  const handleSizeChange = (tab: TabType, newSize: number) => {
    setPagination(prev => ({ ...prev, [tab]: { ...prev[tab], size: newSize, page: 1 } }));
    loadTabData(tab);
  };

  const handleRestore = async () => {
    if (!project || !token || !project.id) return;
    setIsRestoring(true);
    try {
      await restoreProject(token, project.id);
      toast.success('Project restored successfully');
      await refreshProjects();
      navigate(`/projects/${project.id}`);
    } catch (e) {
      const status = e instanceof Error && 'status' in e ? (e as ApiError).status : undefined;
      if (status === 403) {
        toast.error('You do not have permission to restore this project');
      } else {
        toast.error('Failed to restore project');
      }
      console.error('Restore error:', e);
    } finally {
      setIsRestoring(false);
      setShowRestoreConfirm(false);
    }
  };

  const handleCloned = (newProjectId: string) => {
    setIsCloneModalOpen(false);
    navigate(`/projects/${newProjectId}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading archived project..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafafa]">
        <div className="max-w-md w-full mx-4 bg-white rounded-lg border border-[#eaeaea] p-6">
          <div className="flex items-start gap-3 mb-4">
            <FaLock className="text-red-600 w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <h2 className="text-lg font-semibold text-red-900 mb-2">Access Denied</h2>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/archived')}
            className="w-full px-4 py-2 bg-[#171717] text-white text-sm font-medium rounded-lg hover:bg-[#333]"
          >
            Back to Archived Projects
          </button>
        </div>
      </div>
    );
  }

  if (!project) return null;

  const TABS: { id: TabType; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'action-items', label: 'Action Items' },
    { id: 'project-plan-2', label: 'Project Plan' },
    { id: 'daily-updates', label: 'Daily Updates' },
    { id: 'meeting-notes', label: 'Meeting Notes' },
    { id: 'team', label: 'Team' },
    { id: 'documents', label: 'Documents' },
    { id: 'locations', label: 'Locations' },
  ];

  return (
    <ArchivedProjectProvider projectId={projectId}>
      <div className="min-h-screen bg-[#fafafa]">
        <div className="max-w-[1280px] mx-auto px-6 py-8">
          <div className="bg-white rounded-lg border border-[#eaeaea] shadow-sm">
            {/* Header */}
            <div className="px-6 py-6 border-b border-[#eaeaea]">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <FaArchive className="text-amber-600 w-5 h-5" />
                    <span className="inline-block px-2 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded">
                      Archived
                    </span>
                  </div>
                  <h1 className="text-2xl font-semibold text-[#171717]">{project.name}</h1>
                  {project.description && (
                    <p className="text-sm text-[#888] mt-2">{project.description}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Tab bar */}
            <div className="flex border-b border-[#eaeaea]">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-[#555] hover:text-[#171717]'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="px-6 py-6">
              {tabsLoading && (
                <div className="flex justify-center py-8">
                  <LoadingSpinner text="Loading..." />
                </div>
              )}

              {!tabsLoading && activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-[#888] mb-1">Status</p>
                      <p className="text-sm font-medium text-[#171717]">{getReadableStatus(project.status)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#888] mb-1">Budget</p>
                      <p className="text-sm font-medium text-[#171717]">
                        {project.budget !== undefined && project.budget !== null && project.budget > 0
                          ? `$${project.budget.toLocaleString()}`
                          : 'Not set'}
                      </p>
                    </div>
                    {project.owner_id && (
                      <div>
                        <p className="text-sm text-[#888] mb-1">Owner</p>
                        <p className="text-sm font-medium text-[#171717]">{ownerName || 'Loading...'}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!tabsLoading && activeTab === 'action-items' && actionItems && (
                <>
                  <TabActionItems
                    projectId={projectId}
                    token={token || ''}
                    projectData={project}
                    tokenParsed={tokenParsed}
                    onProjectUpdate={async () => {}}
                    actionItems={actionItems.results || []}
                    readOnly={true}
                    highlightedItemId={undefined}
                  />
                  <DownloadLogPagination
                    page={pagination['action-items'].page}
                    size={pagination['action-items'].size}
                    total={pagination['action-items'].total}
                    onPageChange={(page) => handlePageChange('action-items', page)}
                    onSizeChange={(size) => handleSizeChange('action-items', size)}
                  />
                </>
              )}

              {!tabsLoading && activeTab === 'daily-updates' && dailyUpdates && (
                <>
                  <TabDailyUpdates
                    projectId={projectId}
                    token={token || ''}
                    projectData={project}
                    tokenParsed={tokenParsed}
                    onProjectUpdate={async () => {}}
                    dailyUpdates={dailyUpdates.results || []}
                    readOnly={true}
                    highlightedItemId={undefined}
                  />
                  <DownloadLogPagination
                    page={pagination['daily-updates'].page}
                    size={pagination['daily-updates'].size}
                    total={pagination['daily-updates'].total}
                    onPageChange={(page) => handlePageChange('daily-updates', page)}
                    onSizeChange={(size) => handleSizeChange('daily-updates', size)}
                  />
                </>
              )}

              {!tabsLoading && activeTab === 'meeting-notes' && meetingNotes && (
                <>
                  <TabMeetingNotes
                    projectId={projectId}
                    token={token || ''}
                    projectData={project}
                    tokenParsed={tokenParsed}
                    onProjectUpdate={async () => {}}
                    readOnly={true}
                    highlightedItemId={undefined}
                    projectUsers={[]}
                    archived={true}
                    archivedMeetingNotes={meetingNotes || undefined}
                  />
                  <DownloadLogPagination
                    page={pagination['meeting-notes'].page}
                    size={pagination['meeting-notes'].size}
                    total={pagination['meeting-notes'].total}
                    onPageChange={(page) => handlePageChange('meeting-notes', page)}
                    onSizeChange={(size) => handleSizeChange('meeting-notes', size)}
                  />
                </>
              )}

              {!tabsLoading && activeTab === 'project-plan-2' && (
                <TabProjectPlan
                  projectId={projectId}
                  token={token || ''}
                  projectData={project}
                  tokenParsed={tokenParsed}
                  onProjectUpdate={async () => {}}
                  highlightedItemId={undefined}
                  readOnly={true}
                  archived={true}
                />
              )}

              {!tabsLoading && activeTab === 'team' && project && (
                <ProjectTeam
                  projectData={project}
                  onProjectUpdate={async () => {}}
                  archived={true}
                  projectUsers={projectUsers || undefined}
                />
              )}

              {!tabsLoading && activeTab === 'documents' && projectDocuments && (
                <>
                  {projectDocuments.results && projectDocuments.results.length > 0 ? (
                    <div className="space-y-3">
                      {projectDocuments.results.map((doc: any) => ( // eslint-disable-line @typescript-eslint/no-explicit-any
                        <div key={doc.id} className="border border-[#eaeaea] rounded-lg p-4">
                          <p className="font-medium text-[#171717]">{doc.file_name || doc.name || 'Untitled'}</p>
                          {doc.description && <p className="text-sm text-[#888] mt-1">{doc.description}</p>}
                          {doc.file_type && <p className="text-xs text-[#666] mt-2">Type: {doc.file_type}</p>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-[#888]">
                      <p>No documents found</p>
                    </div>
                  )}
                  <DownloadLogPagination
                    page={pagination['documents'].page}
                    size={pagination['documents'].size}
                    total={pagination['documents'].total}
                    onPageChange={(page) => handlePageChange('documents', page)}
                    onSizeChange={(size) => handleSizeChange('documents', size)}
                  />
                </>
              )}

              {!tabsLoading && activeTab === 'locations' && project && (
                <>
                  <ProjectLocations
                    projectId={projectId}
                    projectData={project}
                    onProjectUpdate={async () => {}}
                    readOnly={true}
                    archived={true}
                  />
                  <DownloadLogPagination
                    page={pagination['locations'].page}
                    size={pagination['locations'].size}
                    total={pagination['locations'].total}
                    onPageChange={(page) => handlePageChange('locations', page)}
                    onSizeChange={(size) => handleSizeChange('locations', size)}
                  />
                </>
              )}
            </div>

            {/* Actions */}
            <div className="px-6 py-6 border-t border-[#eaeaea] space-y-4">
            <div className="flex items-center space-x-3 mb-4">
              <FaClipboardList className="text-blue-600 w-5 h-5" />
              <h3 className="text-lg font-semibold text-blue-900">Actions</h3>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => setIsCloneModalOpen(true)}
                className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <FaClipboardList className="w-4 h-4" />
                Clone Project
              </button>

              <button
                onClick={() => setShowRestoreConfirm(true)}
                className="w-full px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
              >
                <FaArchive className="w-4 h-4" />
                Restore Project
              </button>
            </div>

              <button
                onClick={() => navigate('/archived')}
                className="w-full px-4 py-2 bg-[#f5f5f5] text-[#555] text-sm font-medium rounded-lg hover:bg-[#eee] transition-colors"
              >
                Back to Archived Projects
              </button>
            </div>
          </div>
        </div>

        {/* Restore confirmation dialog */}
        <ConfirmationDialog
          isOpen={showRestoreConfirm}
          title="Restore Project"
          message={`Are you sure you want to restore "${project.name}"? It will move back to your active projects list.`}
          confirmText="Restore"
          onConfirm={handleRestore}
          onClose={() => setShowRestoreConfirm(false)}
          isLoading={isRestoring}
        />

        {/* Clone modal */}
        <CloneProjectModal
          isOpen={isCloneModalOpen}
          onClose={() => setIsCloneModalOpen(false)}
          sourceProject={project}
          onCloned={handleCloned}
        />
      </div>
    </ArchivedProjectProvider>
  );
};
